import type { IceTransportBrief } from './iceTransportSummary';
import type { RtcMediaStatsData } from './rtcTrackStats';

/** Bump when the JSON shape changes (evolu-meet-api should version migrations). */
export const MEETING_TELEMETRY_SCHEMA_VERSION = 3 as const;

export type TelemetrySampleEventType = 'connectionQualityChanged' | 'trackSubscribed';

/** Point-in-time room events since the previous telemetry POST (bounded in the hook). */
export interface TelemetrySampleEvent {
  type: TelemetrySampleEventType;
  at: string;
  participantIdentity?: string;
  isLocal?: boolean;
  connectionQuality?: string;
  trackKind?: string;
  trackSource?: string;
  publisherIdentity?: string;
}

/**
 * Payload sent from each browser to POST /api/meeting-telemetry (proxied to evolu-meet-api).
 * One sample per interval per client; `sampleEvents` holds async room events since last sample.
 */
export interface MeetingTelemetryPayload {
  schemaVersion: typeof MEETING_TELEMETRY_SCHEMA_VERSION;
  recordedAt: string;
  /** Random id per page load; helps dedupe retries on the server */
  clientInstanceId: string;
  room: {
    name: string;
    /** LiveKit room state when sampled */
    state: string;
    numParticipants?: number;
  };
  participant: {
    identity: string;
    name?: string;
    /** Parsed JSON from LiveKit participant metadata when valid */
    metadata?: Record<string, unknown>;
    connectionQuality: string;
    /** LiveKit 0..1 — local mic energy (subscriber remotes often stay 0 here) */
    audioLevel?: number;
  };
  tracks: {
    camera: { enabled: boolean };
    microphone: { enabled: boolean };
    screenShare: { enabled: boolean };
  };
  /** Legacy-style combined RTC stats (local publish path + bundled mic on camera branch) */
  media: {
    camera: RtcMediaStatsData | null;
    screenShare: RtcMediaStatsData | null;
  };
  /** ICE / path visibility (host vs relay, RTT on selected pair) */
  ice?: {
    publisher?: IceTransportBrief;
    subscriber?: IceTransportBrief;
  };
  /**
   * Normalised network view for dashboards (RTT mean, ICE path, loss/jitter uplink vs downlink).
   * Downlink aggregates are worst-case across remotes (max loss / max jitter) plus per-remote detail in `mediaDownlink`.
   */
  network?: {
    rttMsAvg?: number;
    /** Selected pair — `local` candidate type per PC (host | srflx | relay | prflx) */
    iceLocalCandidateType?: { publisher?: string; subscriber?: string };
    packetLossPercent?: {
      uplink: { video?: number; audio?: number };
      downlink: { video?: number; audio?: number };
    };
    /** WebRTC RTP `jitter` (seconds) when exposed by the browser */
    jitterSeconds?: {
      uplink: { video?: number; audio?: number };
      downlink: { video?: number; audio?: number };
    };
    /** Instantaneous bitrates (bps) from RTC byte deltas */
    bitrateBps?: {
      uplink: { video?: number; audio?: number };
      /** Sum across all subscribed remote camera+audio paths */
      downlink: { video?: number; audio?: number };
    };
  };
  /** Inbound stats per remote (this browser’s subscriptions). */
  mediaDownlink?: Array<{
    identity: string;
    name?: string;
    camera: RtcMediaStatsData | null;
  }>;
  sampleEvents?: TelemetrySampleEvent[];
  client: {
    userAgent: string;
    language: string;
    hardwareConcurrency?: number;
    deviceMemoryGb?: number;
    virtualBackgroundActive?: boolean;
    noiseCancellationEnabled?: boolean;
    activeRemoteVideoSubscriptions?: number;
  };
}
